/**
 * The document a company's terms of service are, and what it turns into.
 *
 * Terms are written in a rich text editor and read wherever the offer is read.
 * Today that is one place — the proposal, which the builder previews and the
 * portal serves, both from `termsToHtml` below. It will not stay one place:
 * the proposal PDF is a page in the plan, and `@react-pdf/renderer` takes
 * primitives rather than HTML, so a stored string would have to be parsed back
 * apart — an HTML parser nobody wants to own, on the path that produces the
 * document a customer signs. So the stored form is a ProseMirror document: a
 * tree of nodes and marks that every renderer walks, from one source that
 * cannot disagree with itself.
 *
 * **Everything here is an allow-list**, for the same reason `rich-text.ts` is:
 * terms are written by one person and rendered to every customer, which is the
 * shape of every stored-XSS bug there has ever been. The editor's own schema
 * constrains what *it* produces and is worth nothing — a client can POST
 * whatever it likes — so `sanitizeTermsDoc` runs on the server on every write
 * and is the only thing standing between a hostile document and a proposal.
 *
 * The HTML renderer is safe by construction rather than by filtering: it emits
 * tags from this file's own list and escapes every piece of text, so there is
 * no path by which stored markup becomes markup on a page.
 */
export const EMPTY_TERMS = { type: 'doc', content: [] };
/**
 * What a paragraph of terms may be made of.
 *
 * Headings stop at three: terms with a fourth level of nesting are terms
 * nobody reads, and the proposal has no type scale below that anyway.
 */
const BLOCKS = new Set([
    'paragraph', 'heading', 'bulletList', 'orderedList', 'listItem',
    'blockquote', 'horizontalRule',
]);
const MARKS = new Set(['bold', 'italic', 'underline', 'strike', 'link']);
/** Where a link may point. A quote's terms have no business opening a file. */
const SCHEMES = ['http://', 'https://', 'mailto:'];
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * A link somebody can follow, or nothing.
 *
 * `javascript:` is the obvious one and the reason this is not a regex on the
 * whole string: a scheme can be hidden behind whitespace, control characters
 * and mixed case, so the value is stripped and lowercased before it is
 * compared, and anything not on the list is dropped rather than escaped.
 */
function safeHref(value) {
    if (typeof value !== 'string')
        return null;
    const clean = [...value].filter((ch) => ch.codePointAt(0) > 0x20).join('').trim();
    if (clean.length === 0 || clean.length > 2000)
        return null;
    const lower = clean.toLowerCase();
    return SCHEMES.some((scheme) => lower.startsWith(scheme)) ? clean : null;
}
function sanitizeMarks(value) {
    if (!Array.isArray(value))
        return undefined;
    const marks = [];
    for (const mark of value) {
        if (!isRecord(mark) || typeof mark.type !== 'string')
            continue;
        if (!MARKS.has(mark.type))
            continue;
        if (mark.type === 'link') {
            const href = safeHref(isRecord(mark.attrs) ? mark.attrs.href : null);
            // A link with nowhere to go is not a link; the text stays either way.
            if (href)
                marks.push({ type: 'link', attrs: { href } });
            continue;
        }
        marks.push({ type: mark.type });
    }
    return marks.length > 0 ? marks : undefined;
}
function sanitizeNode(value, depth) {
    // A document nested deeper than this is a document somebody built to be
    // expensive to render rather than to be read.
    if (depth > 8 || !isRecord(value) || typeof value.type !== 'string')
        return null;
    if (value.type === 'text') {
        const text = typeof value.text === 'string' ? value.text : '';
        if (text.length === 0)
            return null;
        const marks = sanitizeMarks(value.marks);
        return marks ? { type: 'text', text, marks } : { type: 'text', text };
    }
    if (!BLOCKS.has(value.type))
        return null;
    const node = { type: value.type };
    if (value.type === 'heading') {
        const level = isRecord(value.attrs) ? Number(value.attrs.level) : 1;
        node.attrs = { level: level >= 1 && level <= 3 ? Math.trunc(level) : 1 };
    }
    if (value.type !== 'horizontalRule') {
        const content = Array.isArray(value.content)
            ? value.content
                .map((child) => sanitizeNode(child, depth + 1))
                .filter((child) => child !== null)
            : [];
        node.content = content;
    }
    return node;
}
/**
 * A stored document, with everything not on the allow-list gone.
 *
 * Total: anything at all can be handed to it — a string, null, a deeply
 * nested object a client built by hand — and what comes back is a document
 * this file's own renderers can walk. It never throws, because the caller is
 * a request handler and "the terms would not parse" is not a useful thing to
 * tell somebody who pasted from Word.
 */
export function sanitizeTermsDoc(value) {
    if (!isRecord(value) || value.type !== 'doc' || !Array.isArray(value.content)) {
        return { ...EMPTY_TERMS };
    }
    return {
        type: 'doc',
        content: value.content
            .map((node) => sanitizeNode(node, 0))
            .filter((node) => node !== null),
    };
}
// ---------------------------------------------------------------------------
// What it turns into
// ---------------------------------------------------------------------------
const ESCAPES = {
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
const escape = (text) => text.replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
const MARK_TAGS = {
    bold: 'strong', italic: 'em', underline: 'u', strike: 's', link: 'a',
};
function renderText(node) {
    let html = escape(node.text ?? '');
    // Innermost first, so the marks nest in the order they were applied.
    for (const mark of node.marks ?? []) {
        const tag = MARK_TAGS[mark.type];
        html = mark.type === 'link'
            // `noopener` because a customer's terms should not hand the opener a
            // handle on the tab they came from.
            ? `<a href="${escape(mark.attrs?.href ?? '')}" rel="noopener noreferrer nofollow"`
                + ` target="_blank">${html}</a>`
            : `<${tag}>${html}</${tag}>`;
    }
    return html;
}
const BLOCK_TAGS = {
    paragraph: 'p', bulletList: 'ul', orderedList: 'ol', listItem: 'li', blockquote: 'blockquote',
};
function renderNode(node) {
    if (node.type === 'text')
        return renderText(node);
    if (node.type === 'horizontalRule')
        return '<hr />';
    if (node.type === 'heading') {
        const level = Number(node.attrs?.level ?? 1);
        const tag = `h${level >= 1 && level <= 3 ? level : 1}`;
        return `<${tag}>${(node.content ?? []).map(renderNode).join('')}</${tag}>`;
    }
    const tag = BLOCK_TAGS[node.type];
    if (!tag)
        return '';
    return `<${tag}>${(node.content ?? []).map(renderNode).join('')}</${tag}>`;
}
/**
 * The document as HTML, for the portal and the proposal preview.
 *
 * Safe because of where the tags come from, not because of what was filtered
 * out: every tag is one of this file's own, and every piece of text is
 * escaped. Sanitise first — this renders what it is given.
 */
export function termsToHtml(doc) {
    return doc.content.map(renderNode).join('');
}
/**
 * The document as plain text, one block to a line.
 *
 * Stored beside the document so a search can find a phrase in somebody's
 * terms without every row being parsed, and so a plain-text email has
 * something to carry.
 */
export function termsToText(doc) {
    const lines = [];
    const walk = (node, into) => {
        if (node.type === 'text') {
            into.push(node.text ?? '');
            return;
        }
        if (node.type === 'horizontalRule')
            return;
        if (node.type === 'bulletList' || node.type === 'orderedList') {
            for (const child of node.content ?? [])
                walk(child, into);
            return;
        }
        const parts = [];
        for (const child of node.content ?? [])
            walk(child, parts);
        const line = parts.join('').trim();
        if (line)
            into.push(line);
    };
    for (const node of doc.content) {
        const parts = [];
        walk(node, parts);
        // A paragraph's own text arrives as one string; a list arrives as several.
        for (const part of parts) {
            const text = part.trim();
            if (text)
                lines.push(text);
        }
    }
    return lines.join('\n');
}
/** Whether there is anything in it worth showing. */
export function termsAreEmpty(doc) {
    return termsToText(doc).trim().length === 0;
}
/**
 * A single stored `body` read as sections, by its own headings.
 *
 * `terms_of_service` has held one ProseMirror document since `0064`, and the
 * proposal drew it as one collapsed block with "Read all the terms" under it —
 * which is a signature under terms nobody opened. An accordion needs titled
 * parts, and the author has already written them: a set of terms is headings
 * with paragraphs under them, every time.
 *
 * So this splits at the document's **shallowest heading level** and keeps
 * everything else — deeper headings included — with the heading above it. It
 * is a *reader*, not a migration: the rows are untouched and nothing is
 * guessed about what the author meant. When somebody authors real sections
 * (`terms_of_service_sections`, migration `0116`) those are used instead and
 * this is only ever the fallback for a document nobody has revisited — which
 * is why the ids are derived from the position rather than invented: the same
 * document always reads as the same sections, so a `rev` hash over them is
 * stable.
 *
 * **The shallowest level rather than any heading at all**, which is what it
 * split at first, for two reasons that turn out to be one. A set of terms
 * written as four headings with sub-headings under them has four sections and
 * not eleven — the sub-headings are *inside* a section, which is what
 * indenting them said. And `compileTermsBody` clamps a heading inside a
 * section to level 3 precisely so it cannot read back as a section of its
 * own: against a reader that split at every heading that clamp did nothing,
 * so a sectioned document round-tripped through its own compiled `body` grew
 * a section for every sub-heading it had. One rule fixes both, and the
 * shallowest level is the right one because an author whose top level is `h3`
 * still has sections.
 *
 * Content before the first heading becomes an untitled opening section using
 * `leadTitle`, because dropping it would drop the sentence most sets of terms
 * open with.
 */
export function termsToSections(doc, options = {}) {
    return splitTermsDoc(doc).map((group, index) => ({
        id: `s${index + 1}`,
        title: group.title || (options.leadTitle ?? 'Terms'),
        html: termsToHtml(group.body),
        opensFirst: index === 0,
    }));
}
/**
 * The grouping itself, with no opinion about titles or markup.
 *
 * Two callers split a document and they want different things out of it: the
 * customer's accordion wants rendered html, and the editor's split offer
 * wants a `TermsDoc` per section to hand to a `RichEditor`. They had a copy
 * of the grouping each, which is two answers to "what are this document's
 * sections" — so the grouping is here and the titling is theirs: the
 * proposal's lead group takes the set's own name, and the editor's takes
 * **Introduction** when there is anything after it (§8.4).
 */
export function splitTermsDoc(doc) {
    const levels = doc.content
        .filter((node) => node.type === 'heading')
        .map((node) => Number(node.attrs?.level ?? 1));
    const top = levels.length > 0 ? Math.min(...levels) : 1;
    const groups = [];
    for (const node of doc.content) {
        if (node.type === 'heading' && Number(node.attrs?.level ?? 1) === top) {
            groups.push({ title: termsToText({ type: 'doc', content: [node] }).trim(), nodes: [] });
            continue;
        }
        const last = groups[groups.length - 1];
        if (last)
            last.nodes.push(node);
        else
            groups.push({ title: null, nodes: [node] });
    }
    return groups
        /*
         * A heading with nothing under it is a heading, not a section: an
         * accordion row that opens onto nothing is worse than no row.
         */
        .filter((group) => group.nodes.length > 0 || group.title)
        .map((group) => ({
        title: group.title || null,
        body: { type: 'doc', content: group.nodes },
    }));
}
/**
 * Sections back into one document — `docs/proposal.md` §8.4.
 *
 * **The single `body` is still written**, and this is what writes it: a
 * level-2 heading carrying each section's title, followed by that section's
 * own nodes, in order. The result goes through `sanitizeTermsDoc` like any
 * other write.
 *
 * Why keep a compiled copy at all, when the sections are the source of truth?
 * Because everything that reads `terms_of_service.body` today keeps working
 * untouched — `termsToHtml`, `termsToText`, `termsAreEmpty`, the frozen
 * `quote_approvals.terms_body`, the PDF — and because `termsToSections` is
 * the inverse: `termsToSections(compileTermsBody(x))` returns `x`'s titles.
 * A section edit and a whole-document edit can never disagree, because only
 * one of them is ever written by hand.
 *
 * **A heading inside a section is clamped to level 3.** The section's own
 * title owns level 2, and a level-2 heading in the body would read back as a
 * *second* section on the next pass — which is how a document grows a section
 * nobody added every time it is saved.
 */
export function compileTermsBody(sections) {
    const content = [];
    for (const section of sections) {
        const title = section.title.trim();
        if (!title)
            continue;
        content.push({
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: title }],
        });
        for (const node of section.body.content)
            content.push(demote(node));
    }
    return sanitizeTermsDoc({ type: 'doc', content });
}
/** A heading of level 1 or 2 inside a section becomes a level 3. */
function demote(node) {
    if (node.type !== 'heading')
        return node;
    const level = Number(node.attrs?.level ?? 1);
    return { ...node, attrs: { ...node.attrs, level: level < 3 ? 3 : level } };
}
//# sourceMappingURL=terms-doc.js.map